import {
  Component,
  Input,
  OnInit,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { Observable, finalize, fromEvent, Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';

import {
  ReadingGroup,
  ReadingGroupJoinStatus,
  ReadingGroupType,
} from 'shared/models';
import {
  APIError,
  BookReadingGroupsAPIService,
  ReadingGroupsAPIService,
  QueryResult,
} from 'shared/services';

import { ReadingGroupPasscodeDialogComponent } from '../../dialogs/reading-group-passcode-dialog/reading-group-passcode-dialog.component';
import { AlertService, ToastService } from 'shared/ui';

enum OrderType {
  recent = 'recent',
  memberCount = 'member_count',
  memoCount = 'memo_count',
}

@Component({
  selector: 'lib-reading-group-selection',
  templateUrl: './reading-group-selection.component.html',
  styleUrl: './reading-group-selection.component.scss',
})
export class ReadingGroupSelectionComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('groupList') private _groupListElem!: ElementRef<HTMLDivElement>;

  @Input() contentId!: string;
  @Input() allowedSingleMode = false;
  @Input() bookInfo?: { title: string; cover: string; author: string };
  @Input() invitedGroupId?: number; // 초대받은 그룹 ID

  @Output() createGroupButtonClick = new EventEmitter<void>();
  @Output() groupDetailsButtonClick = new EventEmitter<ReadingGroup>();
  @Output() enterGroupButtonClick = new EventEmitter<number>();
  @Output() singleModeButtonClick = new EventEmitter<void>();

  public _JoinStatus = ReadingGroupJoinStatus;

  public _allGroups?: ReadingGroup[];
  public _allGroupsTotal?: number;

  private readonly _limit = 30;
  public _isLoading = false;
  public _searchKeyword?: string;
  public _isScrolled = false;
  public _isSearched = false; // 검색어가 없다는 문구와 그룹이 없다는 문구 뜨는 조건을 다르게 하기위해 검색 자체를 했는지 판단

  public _showPublicGroupOnly = false;
  public _likeOnly = false;
  public _orderBy = OrderType.recent;
  public readonly _orderByOptions = [
    [OrderType.recent, '최근 읽은 순'],
    [OrderType.memoCount, '댓글 많은 순'],
    [OrderType.memberCount, '맴버 많은 순'],
  ];

  private _ownedGroupCount?: number;
  private _isJoining = false;

  private _unsubscriber = new Subject<void>();
  private _initialViewportHeight = 0;
  public _isKeyboardVisible = false;

  constructor(
    private _readingGroupsAPIService: ReadingGroupsAPIService,
    private _bookReadingGroupsAPIService: BookReadingGroupsAPIService,
    private _alertService: AlertService,
    private _toastService: ToastService
  ) {}

  ngOnInit(): void {
    this._loadAllGroups(true);
  }

  ngAfterViewInit(): void {
    this._setupScrollListener();
    this._setupKeyboardDetection();
  }

  ngOnDestroy(): void {
    this._unsubscriber.next();
    this._unsubscriber.complete();
  }

  public get _groupsTotal(): number | undefined {
    return this._allGroupsTotal;
  }

  public get _groups(): ReadingGroup[] | undefined {
    const groups = this._allGroups;
    if (!groups) {
      return undefined;
    }
    return groups;
  }

  public set _groups(groups: ReadingGroup[] | undefined) {
    this._allGroups = groups;
  }

  private get _listEnded(): boolean {
    return (
      this._groupsTotal != null && this._groupsTotal === this._groups?.length
    );
  }

  private _setupScrollListener(): void {
    if (!this._groupListElem) {
      return;
    }

    const element = this._groupListElem.nativeElement;
    element.addEventListener('scroll', () => {
      this._isScrolled = element.scrollTop > 0;
    });
  }

  refreshGroups(): void {
    this._loadAllGroups(true);
  }

  updateGroup(updatedGroup: ReadingGroup): void {
    [this._allGroups].forEach((groups) => {
      const group = groups?.find((g) => g.id === updatedGroup.id);
      if (!group) {
        return;
      }

      group.name = updatedGroup.name;
    });
  }

  _onGroupDetailsButtonClick(index: number): void {
    const group = this._groups?.[index];

    if (!group) {
      return;
    }

    this.groupDetailsButtonClick.emit(group);
  }

  _onEnterGroupButtonClick(groupId: number, index?: number): void {
    this.enterGroupButtonClick.emit(groupId);

    if (index !== undefined) {
      this._onJoinGroupButtonClick(index);
    }
  }

  _onJoinGroupButtonClick(index: number): void {
    const group = this._groups?.[index];

    if (!group) {
      return;
    }

    if (
      group.type === ReadingGroupType.private &&
      group.status !== ReadingGroupJoinStatus.invited
    ) {
      this._openPasscodeDialog(group.id);
      return;
    }

    if (this._isJoining) {
      return;
    }

    this._isJoining = true;

    this._readingGroupsAPIService
      .join(group.id)
      .pipe(finalize(() => (this._isJoining = false)))
      .subscribe({
        next: () => {
          this._onGroupJoined(group.id);
        },
        error: (error: APIError) => {
          this._toastService.openWarning(error.message);
        },
      });
  }

  _onCreateGroupButtonClick(): void {
    if (this._ownedGroupCount != null && this._ownedGroupCount >= 5) {
      this._toastService.open(
        '책 한 권에 한 사람당 최대 5개의 그룹을 만들 수 있습니다.'
      );
      return;
    }

    this.createGroupButtonClick.emit();
  }

  _onSingleModeButtonClick(): void {
    this.singleModeButtonClick.emit();
  }

  _onPasscodeDialogResult(joined: boolean, groupId: number): void {
    joined && this._onGroupJoined(groupId);
  }

  _onSearchOptionsChange(): void {
    this._loadAllGroups(true);
  }

  _onSearchBarSubmit(): void {
    if (!this._searchKeyword) {
      return;
    }

    this._isSearched = true;

    // 모바일에서 키보드를 내리기 위해 현재 포커스 된 요소(input)에서 포커스 해제
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement) {
      activeElement.blur();
    }

    this._onSearchOptionsChange();
  }

  _onClearSearchKeywordButtonClick(): void {
    this._searchKeyword = '';
    this._isSearched = false;
    this._onSearchOptionsChange();
  }

  _onScrollEnd(): void {
    // 로딩 중이거나 리스트가 끝났거나, 결과가 없으면 요청하지 않음
    // 즐겨읽는 그룹책만 보기 체크 클릭시 결과가 거의없을 경우 onScrollEnd 호출로 API 호출이 두 번 되는 이슈 수정을 위한 예외처리
    if (
      this._isLoading ||
      this._listEnded ||
      (this._groups?.length === 0 && this._allGroupsTotal === 0)
    ) {
      return;
    }
    this._loadAllGroups(false);
  }

  private _loadAllGroups(reset: boolean): void {
    this._isLoading = true;

    this._getBookReadingGroups(reset ? 0 : this._groups?.length).subscribe({
      next: (response) => {
        this._isLoading = false;
        // reset일 때는 항상 total을 업데이트
        if (reset) {
          this._allGroupsTotal = response.total;
        } else if (this._allGroupsTotal == null) {
          this._allGroupsTotal = response.total;
        }
        this._allGroups = (
          response.offset === 0 || !this._groups ? [] : this._groups
        ).concat(response.items);

        if (reset) {
          this._groupListElem.nativeElement.scrollTop = 0;
        }
      },
      error: (error: APIError) => {
        this._isLoading = false;
        this._toastService.openWarning(error.message);
      },
    });
  }

  private _getBookReadingGroups(
    offset: number = 0
  ): Observable<QueryResult<ReadingGroup>> {
    return this._bookReadingGroupsAPIService.get(
      this.contentId,
      offset,
      this._limit,
      this._searchKeyword,
      this._orderBy,
      this._showPublicGroupOnly,
      this._likeOnly
    );
  }

  private _onGroupJoined(groupId: number): void {
    if (!this._allGroups) {
      return;
    }

    const group = this._allGroups.find((group) => group.id === groupId);

    if (!group) {
      return;
    }

    group.status = ReadingGroupJoinStatus.joined;
    group.member_count++;

    this._toastService.open('그룹책 읽기가 시작되었습니다.');
  }

  private _openPasscodeDialog(groupId: number): void {
    const dialogRef = this._alertService.open(
      ReadingGroupPasscodeDialogComponent,
      {
        data: {
          groupId,
        },
      }
    );

    dialogRef.afterClosed().subscribe((joined) => {
      joined !== undefined && this._onPasscodeDialogResult(joined, groupId);
    });
  }

  _onGroupExited(): void {
    this._loadAllGroups(true);
  }

  _onLikeStateChanged(groupId: number): void {
    const group = this._allGroups?.find((g) => g.id === groupId);

    if (!group) {
      return;
    }

    const previousIsLiked = group.is_liked;
    group.is_liked = previousIsLiked === 1 ? 0 : 1;

    // 즐겨읽는 그룹책만 보기 필터가 활성화되어 있고, 즐겨읽기를 해제한 경우
    if (this._likeOnly && group.is_liked === 0) {
      // 리스트에서 해당 그룹 제거
      const index = this._allGroups?.findIndex((g) => g.id === groupId);
      if (index !== undefined && index !== -1 && this._allGroups) {
        this._allGroups.splice(index, 1);
        // 전체 개수도 감소
        if (this._allGroupsTotal != null) {
          this._allGroupsTotal--;
        }
      }
    }
  }

  private _setupKeyboardDetection(): void {
    // Visual Viewport API 사용 (최신 브라우저)
    if (window.visualViewport) {
      this._initialViewportHeight = window.visualViewport.height;

      fromEvent(window.visualViewport, 'resize')
        .pipe(takeUntil(this._unsubscriber), debounceTime(100))
        .subscribe(() => {
          const currentHeight = window.visualViewport!.height;
          // viewport 높이가 초기 높이보다 150px 이상 작아지면 키보드가 올라온 것으로 판단
          const threshold = 150;
          this._isKeyboardVisible =
            currentHeight < this._initialViewportHeight - threshold;
        });
    } else {
      // Fallback: window.innerHeight 사용
      this._initialViewportHeight = window.innerHeight;

      fromEvent(window, 'resize')
        .pipe(takeUntil(this._unsubscriber), debounceTime(100))
        .subscribe(() => {
          const currentHeight = window.innerHeight;
          const threshold = 150;
          this._isKeyboardVisible =
            currentHeight < this._initialViewportHeight - threshold;
        });
    }
  }
}
